import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../services/api';
import tokenStorage from '../../utils/tokenStorage';
import io from 'socket.io-client';
import logoFull from '../../assets/images/logo_aktivity.png';
import logoIcon from '../../assets/images/standalone_logo.png';

import PreSessionTab from './tabs/PreSessionTab';
import PostSessionTab from './tabs/PostSessionTab';

// Determine socket URL at runtime, mirroring getSocketUrl() in hooks/useWebSocket.js.
// A build-time constant cannot work here: REACT_APP_API_URL is empty in production
// (see frontend/.env.production), so the old `|| 'http://localhost:5000'` fallback
// shipped a localhost socket to every visitor's browser.
const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    // ORIGIN ONLY — socket.io reads a URL path as a NAMESPACE, so passing
    // "http://localhost:5050/api" verbatim dies with "Invalid namespace".
    try {
      return new URL(process.env.REACT_APP_API_URL).origin;
    } catch {
      return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
    }
  }
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';
  if (!isLocalhost) {
    return window.location.origin;
  }
  return 'http://localhost:5000';
};

const SOCKET_URL = getSocketUrl();

const ParticipantSessionHub = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionLivePopup, setSessionLivePopup] = useState(false);
  // 'home' = welcome page, 'pre-session' = inside pre-session, 'post-session' = inside post-session
  const [activeView, setActiveView] = useState('home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const participantData = tokenStorage.getParticipantData(sessionId);
  const participantId = participantData?.participantId || participantData?.id || null;
  const participantName = participantData?.name || null;

  const fetchSession = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/participant/sessions/${sessionId}`);
      const sessionData = response?.data || response;

      if (sessionData) {
        setSession(sessionData);
        // If session is live, redirect to active page
        if (sessionData.status === 'live' || sessionData.status === 'paused') {
          navigate(`/session/${sessionId}/lobby`, { replace: true });
          return;
        }
        // If no pre-session, go to lobby
        if (!sessionData.preSession?.enabled && sessionData.status !== 'completed') {
          navigate(`/session/${sessionId}/lobby`, { replace: true });
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Socket for session going live
  useEffect(() => {
    if (!sessionId) return;
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('join-session', sessionId, { participantId, participantName, role: 'participant' });
    });
    socket.on('session-status-changed', (data) => {
      if (data.status === 'live') {
        setSessionLivePopup(true);
        setSession(prev => prev ? { ...prev, status: 'live' } : prev);
      } else if (data.status === 'completed') {
        setSession(prev => prev ? { ...prev, status: 'completed' } : prev);
      }
    });
    return () => { socket.disconnect(); };
  }, [sessionId, participantId, participantName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Session not found</p>
          <button onClick={() => navigate('/join')} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm">Go to Join Page</button>
        </div>
      </div>
    );
  }

  const isPreSessionEnabled = session.preSession?.enabled;
  const isPostSessionEnabled = session.postSession?.enabled;
  const isLive = session.status === 'live' || session.status === 'paused';
  const isCompleted = session.status === 'completed';

  // If user clicks into pre-session, render PreSessionTab with its own sidebar
  if (activeView === 'pre-session') {
    return (
      <div className="relative">
        {/* Session Live Popup */}
        <AnimatePresence>
          {sessionLivePopup && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-sm mx-4 overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5 text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="w-8 h-8 bg-white rounded-full animate-pulse"></span>
                  </div>
                  <h2 className="text-xl font-bold text-white">Session is Live!</h2>
                  <p className="text-green-100 text-sm mt-1">Your trainer has started the session</p>
                </div>
                <div className="p-6 text-center">
                  <p className="text-sm text-neutral-600 mb-5">Join now to participate in activities, polls, and quizzes.</p>
                  <button onClick={() => navigate(`/session/${sessionId}/active`)}
                    className="w-full py-3 bg-green-500 text-white rounded-xl text-sm font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span> Join Live Session
                  </button>
                  <button onClick={() => setSessionLivePopup(false)}
                    className="w-full mt-2 py-2 text-sm text-neutral-500 hover:text-neutral-700">Continue Pre-Session</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <PreSessionTab
          sessionId={sessionId}
          participantId={participantId}
          readOnly={isCompleted || isLive}
          sessionStatus={session.status}
          hasPostSession={isPostSessionEnabled}
          onBack={() => setActiveView('home')}
        />
      </div>
    );
  }

  // If user clicks into post-session
  if (activeView === 'post-session' && isCompleted && isPostSessionEnabled) {
    return (
      <PostSessionTab sessionId={sessionId} participantId={participantId} />
    );
  }

  // ==================== HOME / WELCOME VIEW ====================
  // Sidebar: Pre-Session, Live Session (locked), Post-Session (locked)

  const homeNavItems = [
    {
      id: 'pre-session',
      name: 'Pre-Session',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      enabled: isPreSessionEnabled,
      onClick: () => isPreSessionEnabled && setActiveView('pre-session'),
    },
    {
      id: 'live-session',
      name: 'Live Session',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
        </svg>
      ),
      enabled: isLive || isCompleted,
      locked: !isLive && !isCompleted,
      badge: isLive ? 'Live' : null,
      onClick: () => {
        if (isLive) navigate(`/session/${sessionId}/active`);
        else if (isCompleted) navigate(`/session/${sessionId}/lobby`);
        else toast('Session hasn\'t started yet', { icon: '⏳' });
      },
    },
    {
      id: 'post-session',
      name: 'Post-Session',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
        </svg>
      ),
      enabled: isCompleted && isPostSessionEnabled,
      locked: !isCompleted,
      onClick: () => {
        if (isCompleted && isPostSessionEnabled) setActiveView('post-session');
        else toast('Available after session ends', { icon: '🔒' });
      },
    },
  ].filter(item => {
    // Only show post-session if it's enabled
    if (item.id === 'post-session' && !isPostSessionEnabled) return false;
    return true;
  });

  const bottomNavItems = [];

  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img src={logoFull} alt="Aktivity" className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }} />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {homeNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    item.locked
                      ? 'text-neutral-300 cursor-not-allowed'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap">{item.name}</motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {item.badge}
                    </span>
                  )}
                  {!sidebarCollapsed && item.locked && (
                    <span className="ml-auto">
                      <svg className="w-4 h-4 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Nav */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}>
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap">{item.name}</motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors">
            <svg className="w-5 h-5 transition-transform" style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">Welcome</h1>
            </div>
            <div className="flex items-center gap-3">
              {session?.code && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                  <span className="text-xs text-neutral-500">Code:</span>
                  <span className="text-xs font-mono font-semibold text-neutral-900">#{session.code}</span>
                </div>
              )}
              <button onClick={() => navigate('/join')}
                className="px-3 py-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors">
                Leave
              </button>
            </div>
          </div>
        </header>

        {/* Welcome Content */}
        <main className="flex-1 p-4 sm:p-6 bg-white overflow-y-auto pb-24 lg:pb-6">
          {/* Welcome Header */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">
                    Welcome, {participantName || 'Participant'}!
                  </h1>
                  <p className="text-neutral-500 text-sm">You're all set for this session</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full ${
                isLive ? 'bg-green-100 text-green-700' :
                isCompleted ? 'bg-blue-100 text-blue-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`}></span>
                {isLive ? 'Live' : isCompleted ? 'Completed' : 'Scheduled'}
              </span>
            </div>
            <div className="pt-4 border-t border-neutral-200">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">Session</p>
                  <h2 className="text-lg font-bold text-neutral-900 break-words">{session.name}</h2>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-neutral-400 uppercase tracking-wider">Code</p>
                  <p className="text-xl font-mono font-bold text-neutral-900">#{session.code}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Phase Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Pre-Session Card */}
            {isPreSessionEnabled && (
              <button
                onClick={() => setActiveView('pre-session')}
                className="bg-white rounded-xl border border-neutral-200 p-6 text-left hover:border-primary-300 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-neutral-900 mb-1">Pre-Session</h3>
                <p className="text-sm text-neutral-500">Complete preparation tasks before the training begins</p>
                <div className="mt-4 flex items-center gap-2 text-primary-600 text-sm font-medium">
                  Open <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
                </div>
              </button>
            )}

            {/* Live Session Card */}
            <div className={`bg-white rounded-xl border p-6 text-left ${
              (isLive || isCompleted) ? 'border-green-300 hover:shadow-md cursor-pointer' : 'border-neutral-200 opacity-60'
            }`}
              onClick={() => {
                if (isLive) navigate(`/session/${sessionId}/active`);
                else if (isCompleted) navigate(`/session/${sessionId}/lobby`);
              }}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${(isLive || isCompleted) ? 'bg-green-100' : 'bg-neutral-100'}`}>
                <svg className={`w-6 h-6 ${(isLive || isCompleted) ? 'text-green-600' : 'text-neutral-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                </svg>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-bold text-neutral-900">{isCompleted ? 'Session Report' : 'Live Session'}</h3>
                {isLive && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>}
              </div>
              <p className="text-sm text-neutral-500">
                {isLive ? 'Session is live! Join now' : isCompleted ? 'View your reports, activities & workbooks' : 'Will be available when the trainer starts the session'}
              </p>
              {!isLive && !isCompleted && (
                <div className="mt-4 flex items-center gap-2 text-neutral-400 text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  Locked
                </div>
              )}
              {isCompleted && (
                <div className="mt-4 flex items-center gap-2 text-green-600 text-sm font-medium">
                  View <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
                </div>
              )}
            </div>

            {/* Post-Session Card */}
            {isPostSessionEnabled && (
              <div className={`bg-white rounded-xl border p-6 text-left ${
                isCompleted ? 'border-purple-300 hover:shadow-md cursor-pointer' : 'border-neutral-200 opacity-60'
              }`}
                onClick={() => {
                  if (isCompleted) setActiveView('post-session');
                }}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isCompleted ? 'bg-purple-100' : 'bg-neutral-100'}`}>
                  <svg className={`w-6 h-6 ${isCompleted ? 'text-purple-600' : 'text-neutral-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-neutral-900 mb-1">Post-Session</h3>
                <p className="text-sm text-neutral-500">
                  {isCompleted ? 'Track action plans and reflections' : 'Available after the session ends'}
                </p>
                {!isCompleted && (
                  <div className="mt-4 flex items-center gap-2 text-neutral-400 text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    Locked
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {homeNavItems.map((item) => (
            <button key={item.id} onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                item.locked ? 'text-neutral-300' : 'text-neutral-600'
              }`}>
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'pre-session' ? 'Pre' : item.id === 'live-session' ? 'Live' : 'Post'}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Session Live Popup (on home view) */}
      <AnimatePresence>
        {sessionLivePopup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-sm mx-4 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="w-8 h-8 bg-white rounded-full animate-pulse"></span>
                </div>
                <h2 className="text-xl font-bold text-white">Session is Live!</h2>
              </div>
              <div className="p-6 text-center">
                <button onClick={() => navigate(`/session/${sessionId}/active`)}
                  className="w-full py-3 bg-green-500 text-white rounded-xl text-sm font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span> Join Live Session
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ParticipantSessionHub;
